import { Readability } from "@mozilla/readability";
import React from "react";
import { createRoot } from "react-dom/client";
import { StyleSheetManager } from "styled-components";
import {
  DisplayMode,
  ScenarioData,
  ScenarioFunction,
  SessionEvent,
  VoiceChatProps,
  APP_IDENTIFIER,
} from "../components/VoiceChat/types";
import { ChatVisibility } from "../components/VoiceChat/fictiveTypes";

export const SILENT_AUDIO_DATA_URI =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQQAAAAAAA==";

/**
 * Reads the current page using Readability to attempt to extract data about the document.
 * As we run this on the client, we can see the actual rendered page.
 *
 * @param scenarioId
 * @returns
 */
export function readPageToCustomScenarioData(scenarioId: string) {
  const clone = document.cloneNode(true) as Document;
  const article = new Readability(clone).parse();
  if (!article) return {};
  return {
    documents: {
      // Need a unique ID but won't currently be saved
      a: {
        // Make namespace unique to both scenario and page so we don't
        // leak data between pages
        namespace: scenarioId + " " + window.location.href,
        uri: window.location.href,
        title: article.title,
        mimeType: "text/html",
        excerpt: article.excerpt,
        byline: article.byline,
        lang: article.lang,
        created: article.publishedTime,
        content: article.content,
        isActive: true, // tells backend to actually use this doc
      },
    },
  };
}

export function renderInShadowRoot(
  rootElement: HTMLElement,
  component: React.FunctionComponent,
  props: React.Attributes,
  closed: boolean = true
) {
  // mode: closed means host page javascript cannot access the shadow DOM
  const shadowRoot = rootElement.attachShadow({
    mode: closed ? "closed" : "open",
  });
  const renderRoot = createRoot(shadowRoot);
  renderRoot.render(
    React.createElement(StyleSheetManager, { target: shadowRoot }, React.createElement(component, props))
  );
  return () => renderRoot.unmount();
}

/**
 * Shortens an error message for a screen reader, best effort: drops a raw payload (JSON or a Python dict
 * starting with "{" and a quote), which for provider errors may also contain key fragments, and caps the
 * length at a word boundary. This is not a reliable redaction of secrets in free text.
 */
export function summarizeErrorForAnnouncement(message: string, maxLength: number = 100): string {
  const payloadStart = message.search(/(^|[\s:-])\{\s*["']/);
  const summary = (payloadStart === -1 ? message : message.slice(0, payloadStart)).replace(/[\s:,-]+$/, "");
  // Cut by code point, so text without spaces (e.g. Japanese, long IDs) is not split inside a character
  const chars = Array.from(summary);
  if (chars.length <= maxLength) return summary;
  const cut = chars.slice(0, maxLength).join("");
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function truncateLargeFields(obj: unknown, maxLength: number = 500): unknown {
  const seen = new WeakSet<object>();

  function helper(val: unknown): unknown {
    if (val === null || typeof val !== "object") {
      if (typeof val === "string" && val.length > maxLength) {
        return val.slice(0, maxLength) + "…";
      }

      if (typeof val === "function") {
        return "[Function]";
      }

      return val;
    }

    if (seen.has(val as object)) return "[Circular]";
    seen.add(val as object);

    // DOM elements
    if (typeof window !== "undefined" && val instanceof window.Element) {
      try {
        let markup = new window.XMLSerializer().serializeToString(val);
        if (markup.length > maxLength) markup = markup.slice(0, maxLength) + "…";
        return `[DOMMarkup: ${markup}]`;
      } catch {
        let desc = `[HTMLElement ${val.tagName}`;
        if (val.id) desc += `#${val.id}`;
        if (val.className) desc += `.${val.className}`;
        desc += "]";
        return desc;
      }
    }

    // React element heuristic (type-safe)
    if (typeof val === "object" && val !== null && "$$typeof" in val && "_owner" in val) {
      return "[ReactElement]";
    }

    if (Array.isArray(val)) {
      return (val.length > maxLength ? val.slice(0, maxLength) : val).map(helper);
    }

    if (ArrayBuffer.isView(val)) {
      // Typed arrays: slice and return a new instance of the same type
      if (!(val instanceof DataView)) {
        const typedArray = val as
          | Uint8Array
          | Int8Array
          | Uint16Array
          | Int16Array
          | Uint32Array
          | Int32Array
          | Float32Array
          | Float64Array
          | Uint8ClampedArray
          | BigInt64Array
          | BigUint64Array;
        return typedArray.length > maxLength ? typedArray.slice(0, maxLength) : typedArray;
      } else {
        // For DataView or unknown, just return a string summary
        return `[${val.constructor.name}]`;
      }
    }

    if (Object.getPrototypeOf(val) === Object.prototype) {
      const result: Record<string, unknown> = {};
      const objVal = val as Record<string, unknown>;

      for (const key in objVal) {
        try {
          result[key] = helper(objVal[key]);
        } catch {
          result[key] = "[Error processing value]";
        }
      }
      return result;
    }

    // Fallback: try JSON serialization for custom objects
    try {
      const str = JSON.stringify(val);
      const truncated = str.length > maxLength ? str.slice(0, maxLength) + "…" : str;
      return `[Serialized: ${truncated}]`;
    } catch {
      return `[Unserializable: ${val.constructor?.name || typeof val}]`;
    }
  }

  return helper(obj);
}

/**
 * The Widget can be configured with data-attributes, which are string only. Not all props are supported
 * via string. We also need to convert the boolean ones to actual booleans.
 * @param props
 * @returns
 */
export function parseStringProps(dataset: DOMStringMap): Partial<VoiceChatProps> {
  const props: Partial<VoiceChatProps> = {};
  props.session = {};
  props.scenario = {};
  props.session.botAudioEnabled = dataset.botAudioEnabled !== "false"; // Default true
  props.session.askConsent = dataset.askConsent === "true";
  props.session.autostart = dataset.autostart === "true";
  props.session.debug = dataset.debug === "true";
  props.session.showWordsAsTheyAreSpoken = dataset.showWordsAsTheyAreSpoken !== "false";
  props.session.disableTextInput = dataset.disableTextInput === "true";
  props.session.disableChatLog = dataset.disableChatLog === "true";
  // New enum-based properties
  props.session.chatInputVisibility = dataset.chatInputVisibility as ChatVisibility | undefined;
  props.session.chatLogVisibility = dataset.chatLogVisibility as ChatVisibility | undefined;
  props.session.microphoneStartUnmuted = dataset.microphoneStartUnmuted === "true";
  props.enableUserCamera = dataset.enableUserCamera === "true";
  // Can only take string here, even if the prop supports functions
  props.sessionReportUrl = dataset.sessionReportUrl;
  props.session.playerId = dataset.playerId;
  props.session.organizationId = dataset.organizationId;
  props.session.scenarioId = dataset.scenarioId;
  props.session.fetchScenario = dataset.fetchScenario !== "false";
  props.session.apiKey = dataset.apiKey;
  props.session.uiLocale = dataset.uiLocale || dataset.locale; // Use uiLocale if available, fallback to locale
  // Not supported as string prop
  // props.session.getIdToken = async () => dataset.idToken;
  if (dataset.avatarUrl !== undefined) props.scenario.avatarUrl = dataset.avatarUrl;
  if (dataset.backgroundUrl !== undefined) props.scenario.backgroundUrl = dataset.backgroundUrl;
  if (dataset.botName !== undefined) props.scenario.botName = dataset.botName;
  if (dataset.botImageUrl !== undefined) props.scenario.botImageUrl = dataset.botImageUrl;
  if (dataset.consentMessage !== undefined) props.scenario.consentMessage = dataset.consentMessage;
  if (dataset.description !== undefined) props.scenario.description = dataset.description;
  if (dataset.initialStep !== undefined) props.scenario.initialStep = dataset.initialStep;
  if (dataset.locale !== undefined) props.scenario.locale = dataset.locale;
  if (dataset.title !== undefined) props.scenario.title = dataset.title;
  if (dataset.offline !== undefined) props.scenario.offline = dataset.offline === "true";
  props.displayMode = dataset.displayMode as DisplayMode | undefined; // Keep as string, handle in component
  props.helpUrl = dataset.helpUrl;
  props.serverUrl = dataset.serverUrl;
  if (dataset.widgetHint !== undefined) props.scenario.widgetHint = dataset.widgetHint;
  props.mountInElement = dataset.mountInElement
    ? (document.getElementById(dataset.mountInElement) ?? undefined)
    : undefined;

  return props;
}

export function ensureVoiceChatDefaults(props: VoiceChatProps): VoiceChatProps {
  const scenarioDefaults: ScenarioData = {
    locale: "en-US",
  };

  let mergedSession = {
    ...(props.session || {}),
    playerId: props.session?.playerId || getOrCreateAnonymousId(),
  };

  // Migrate old boolean properties to new enum properties for backward compatibility
  if (mergedSession.chatInputVisibility === undefined && mergedSession.disableTextInput !== undefined) {
    mergedSession.chatInputVisibility = mergedSession.disableTextInput ? ChatVisibility.Disabled : ChatVisibility.Shown;
  }
  if (mergedSession.chatLogVisibility === undefined && mergedSession.disableChatLog !== undefined) {
    mergedSession.chatLogVisibility = mergedSession.disableChatLog ? ChatVisibility.Disabled : ChatVisibility.Shown;
  }

  const mergedScenario = {
    ...scenarioDefaults,
    ...(props.scenario || {}),
    description: props.scenario?.description || props.scenario?.title, // Use title as fallback for description
  };

  return {
    ...props,
    session: mergedSession,
    scenario: mergedScenario,
    displayMode: props.displayMode || DisplayMode.widget, // Default to widget
    serverUrl: props.serverUrl || import.meta.env.VITE_SERVER_URL,
    enableUserCamera: props.enableUserCamera ?? false,
  };
}

export function getCreateSessionUrl(serverUrl: string): string {
  const cleanedBase = serverUrl.replace(/\/$/, ""); // Remove trailing slash if any
  return `${cleanedBase}/sessions`;
}

export function getCreateWebRTCOfferUrl(serverUrl: string, sessionId: string): string {
  const cleanedBase = serverUrl.replace(/\/$/, ""); // Remove trailing slash if any
  return `${cleanedBase}/sessions/${sessionId}/offer`;
}

export function getWebsocketUrl(serverUrl: string, sessionId: string): string {
  const url = new URL(serverUrl);
  const wsProtocol = url.protocol === "https:" ? "wss:" : "ws:";
  const isLocalhost = url.hostname === "localhost";

  let pathPrefix = "";
  if (!isLocalhost) {
    pathPrefix = "/ws/";
  }

  let newPath = `${pathPrefix}${url.pathname}/sessions/${sessionId}`;
  newPath = newPath.replace(/\/\//g, "/"); // Remove double slashes

  return `${wsProtocol}//${url.host}${newPath}`;
}

export function getFetchScenarioUrl(serverUrl: string, scenarioId: string): string {
  const cleanedBase = serverUrl.replace(/\/$/, ""); // Remove trailing slash if any
  return `${cleanedBase}/scenario/${scenarioId}/data`;
}

export function getEnvironment(serverUrl: string): string {
  if (!serverUrl) {
    return "local-dev";
  }
  const url = new URL(serverUrl);
  const hostname = url.hostname;

  if (hostname === "localhost") {
    return "local-dev";
  }

  const parts = hostname.split(".");
  // If more than 2 parts (e.g., sub.domain.com), return the first part (subdomain).
  // Otherwise (e.g., domain.com or singleword), return the full hostname.
  if (parts.length > 2) {
    return parts[0];
  } else {
    return hostname;
  }
}

export function getFullVersionString() {
  const ua = navigator.userAgent || "";
  let browserName = "UnknownBrowser";
  let osName = "UnknownOS";

  // Detect OS
  if (ua.indexOf("Windows") > -1) osName = "Win";
  else if (ua.indexOf("Mac") > -1) osName = "Mac";
  else if (ua.indexOf("Linux") > -1) osName = "Linux";
  else if (ua.indexOf("Android") > -1) osName = "Android";
  else if (ua.indexOf("iOS") > -1 || /iPhone|iPad|iPod/.test(ua)) osName = "iOS";

  // Detect browser
  if (ua.indexOf("Chrome") > -1) browserName = "Chrome";
  else if (ua.indexOf("Safari") > -1) browserName = "Safari";
  else if (ua.indexOf("Firefox") > -1) browserName = "Firefox";
  else if (ua.indexOf("MSIE") > -1 || ua.indexOf("Trident/") > -1) browserName = "IE";
  else if (ua.indexOf("Edge") > -1) browserName = "Edge";

  const versionString = import.meta.env.VITE_VERSION || "VoiceChatDev";
  return `${osName}-${browserName}-${versionString}`;
}

/**
 * Checks if a given string value matches a pattern, which can be undefined,
 * an exact string, or a regex string (e.g., "/pattern/i").
 * @param value The string value to check (e.g., message.text).
 * @param pattern The pattern to match against (e.g., transition.text).
 * @returns True if it matches, false otherwise.
 */
function matchesPattern(value: string | undefined, pattern: string | undefined): boolean {
  if (pattern === undefined) {
    return false;
  }
  if (value === undefined) {
    // Pattern is defined, but the message has no value for this property, so it's not a match.
    return false;
  }

  // Check for regex pattern (e.g., /pattern/i)
  if (pattern.startsWith("/") && pattern.lastIndexOf("/") > 0) {
    try {
      // Extract pattern and flags (if any)
      const match = pattern.match(/^\/(.+)\/([gimyus]*)$/);
      if (match) {
        const regex = new RegExp(match[1], match[2]);
        return regex.test(value);
      } else {
        // Fallback for simple cases like '/pattern/' without flags
        const simpleRegex = new RegExp(pattern.slice(1, -1));
        return simpleRegex.test(value);
      }
    } catch {
      console.error(`matchesPattern: Invalid regex pattern "${pattern}" provided.`);
      return false; // Invalid regex fails the match
    }
  } else {
    // Exact string match required
    return value === pattern;
  }
}

/**
 * Checks if a ChatMessage satisfies the conditions defined in a ChatTransition.
 * @param message The incoming ChatMessage event.
 * @param transition The ChatTransition definition to check against.
 * @returns True if the message matches all defined conditions in the transition, false otherwise.
 */
export function doesMessageMatchTransition(message: SessionEvent, transition: ScenarioFunction): boolean {
  const nextStepMatch = message.text === transition.nextStep || message.data === transition.nextStep;

  const typeMatch = matchesPattern(message.eventType.toString(), transition.pattern);
  const senderMatch = matchesPattern(message.role?.toString(), transition.pattern);

  const textMatch = matchesPattern(message.text, transition.pattern);

  const dataMatch = matchesPattern(message.data, transition.pattern);

  return nextStepMatch || typeMatch || senderMatch || textMatch || dataMatch;
}

export function isPlainObject(val: unknown): val is Record<string, unknown> {
  return typeof val === "object" && val !== null && Object.prototype.toString.call(val) === "[object Object]";
}

/**
 * Logs the differences between two flat objects.
 * @param prev The previous object.
 * @param next The next object.
 * @param label Optional label for the log.
 */
export function logFlatObjectDiff<T extends Record<string, unknown>>(prev: T, next: T, label = "Object diff") {
  const allKeys = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);
  const changes: { key: string; from: unknown; to: unknown }[] = [];
  allKeys.forEach(key => {
    if (prev?.[key] !== next?.[key]) {
      changes.push({ key, from: prev?.[key], to: next?.[key] });
    }
  });
  if (changes.length) {
    console.log(`${label}:`, changes);
  }
}

/**
 * Compares two nested objects
 * @param current
 * @param updates
 * @returns
 */
export function extractChanges(current: unknown, updates: unknown): unknown | null {
  if (current === updates || typeof updates === "undefined") {
    return null;
  }
  if (!isPlainObject(updates) && !Array.isArray(updates)) {
    return updates;
  }
  if (Array.isArray(updates)) {
    if (
      !Array.isArray(current) ||
      updates.length !== current.length ||
      !updates.every((val, i) => val === current[i])
    ) {
      return [...updates];
    }
    return null;
  }
  if (isPlainObject(updates)) {
    if (!isPlainObject(current)) {
      return { ...updates };
    }
    const delta: Record<string, unknown> = {};
    let hasChanges = false;
    for (const key in updates) {
      const recursiveChanges = extractChanges(current[key], updates[key]);
      if (recursiveChanges !== null) {
        delta[key] = recursiveChanges;
        hasChanges = true;
      }
    }
    return hasChanges ? delta : null;
  }
  return null;
}

/**
 * Shallow merge where `overrides` wins, except keys with value `undefined`.
 * In other words: `undefined` never overwrites an existing value.
 */
export function mergeExceptUndefineds<T extends Record<string, unknown>>(base: T, overrides?: Partial<T> | null): T {
  if (!overrides) return { ...base };
  const result: T = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    if (value !== undefined) {
      result[key as keyof T] = value as T[keyof T];
    }
  }
  return result;
}

export async function waitFor(
  condition: () => boolean,
  timeoutMs: number = 5000,
  pollIntervalMs: number = 100
): Promise<void> {
  return new Promise<void>(resolve => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (condition()) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - startTime > timeoutMs) {
        clearInterval(interval);
        resolve();
      }
    }, pollIntervalMs);
  });
}

/**
 * Copies a string to the user's clipboard with a fallback for older browsers.
 * Resolves when the copy attempt completes (success or failure).
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (!text) return Promise.resolve();

  // Preferred modern API
  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      console.warn("navigator.clipboard.writeText failed, falling back to execCommand");
    }
  }

  // Fallback for older browsers: create a hidden textarea, select and copy
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    // Prevent scrolling to bottom for iOS
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.width = "1px";
    textarea.style.height = "1px";
    textarea.style.padding = "0";
    textarea.style.border = "none";
    textarea.style.outline = "none";
    textarea.style.boxShadow = "none";
    textarea.style.background = "transparent";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (!successful) console.warn("Fallback copy using execCommand returned false");
  } catch (err) {
    console.warn("Fallback copy failed", err);
  }
}

export function getOrCreateAnonymousId() {
  const key = APP_IDENTIFIER + "-anon-id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, `anon_${id}`);
  }
  return id;
}

export function getNextEnumValue<T extends Record<string, unknown>>(
  currentValue: T[keyof T] | undefined,
  enumObject: T
): T[keyof T] {
  const enumKeys = Object.keys(enumObject).filter(k => isNaN(Number(k)));
  const enumValues = enumKeys.map(k => enumObject[k]) as T[keyof T][];

  const currentIndex = currentValue !== undefined ? enumValues.indexOf(currentValue) : -1;
  const nextIndex = (currentIndex + 1) % enumValues.length;
  return enumValues[nextIndex];
}

export function isScreenshotSupported(): boolean {
  return (
    !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) &&
    // @ts-expect-error ImageCapture not yet standard type
    typeof ImageCapture?.prototype?.grabFrame === "function"
  );
}

// Utility function to download text as a file
export const downloadTextAsFile = (text: string, filename: string) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export async function requestMicrophonePermission(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return false;
  }

  let stream: MediaStream | undefined;

  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return true;
  } catch (error) {
    console.warn("Microphone permission denied or failed:", error);
    return false;
  } finally {
    stream?.getTracks().forEach(track => track.stop());
  }
}
